import { makeTile } from './tile';
import { SimpleMap } from 'shared/stores/core/SimpleMap';
import { getHiDpiRatio } from '../../../render-settings';

const MINIMUM_TILE_SIZE = 500;
const FRAME_SIZE = 1;

/**
 * Handles rendering the viewport's tiles.
 * @param viewport
 * @constructor
 */
export function ViewportTileLayer (viewport) {
  // The concept behind this class is fairly straightforward, assuming you already understand the pyramid (if you don't
  // ask someone on the viewer team). An outer div serves as a container that represents the entire rendering surface
  // of the base image. This div can be (and usually is) extremely large (80k x 80k px, or even larger). The tiles are
  // simply scaled and placed in their rightful position on this div. Obviously for performance reasons we only render
  // tiles that are actually visible in the viewport, and pop them off when they're not.

  // The giant container. The Renderer class handles sizing this element.
  const container = document.createElement('div');
  // Reference to the viewport div so we can compute the visible area of the viewport.
  const $viewportDiv = viewport.vpDivGet();

  // The intro description at the top of this class glossed over the fact that there's multiple layers of tiles.
  // Depending on the current zoom level we'll be picking a different tile sizes to reduce the number of http
  // requests. The simplest way to picture this is, at a base resolution of 80k with tiles at 400px you've got a total
  // of 40,000 tiles. If you scale out to "fit" zoom you don't want to request all those tiles. "Layers" allow us to
  // pick smaller images with fewer tiles.
  const layers = viewport.getPyramid().reduce((out, level) => {
    const tileBuffer = new SimpleMap();
    out[level.zoom] = {
      tileBuffer,
      zoom: level.zoom
    };
    return out;
  }, {});
  // This function allows us to pick the "layer" that we want to pull tiles from.
  const getClosestLayer = getClosestLayerFn(layers);

  // Store off references to objects to reduce lookup time.
  const xinfo = viewport.getXinfoObj();
  const vpState = viewport.getVPView();
  const urlBuilder = viewport.urlBuilder;
  const xTileWidth = alignToTileBoundary(xinfo.tileWidth);
  const xTileHeight = alignToTileBoundary(xinfo.tileHeight);

  // Allocate space for primitives outside the paint() function so we don't incur the hit of allocation and garbage
  // collection during the render cycle.
  let layer, lastLayer;
  let vpWidth, vpHeight;
  let viewportZoom, pyramidZoom, scale;
  let tileLeft, tileTop, tileWidth, tileHeight, tilePosition;
  let tileWidthPx, tileHeightPx; // String concatenation is (relatively) expensive so reduce how often we do it.
  let tileL, tileR, tileT, tileB;
  let tile, img;
  let zoomChanged;
  let x, y;

  container.className = 'us-vp-tile-container';

  // Expose the container to the renderer.
  this.container = container;

  // The meat of this class. As much as possible logic is inlined so we don't incur performance hits by making function
  // calls.
  this.paint = () => {
    // First check if zoom has changed; if so we have to do a little more work.
    if (viewportZoom !== vpState.zoom) {
      zoomChanged = true;
      viewportZoom = vpState.zoom;
      layer = getClosestLayer(vpState.zoom / getHiDpiRatio());

      // If we've switched pyramid layers clear the old one
      if (lastLayer && lastLayer !== layer) {
        for (let [, tile] of lastLayer.tileBuffer.items) {
          tile.remove(); // Note IE requires a shim for this call. It's in /lib/utility/browser-compatibility.js
        }
      }

      // Recalculate the size of tiles that will be rendered. Note that they're "cached" too so we don't have
      // to recalculate if zoom doesn't change.
      pyramidZoom = layer.zoom;
      scale = pyramidZoom / viewportZoom;
      tileWidth = xTileWidth * scale;
      tileHeight = xTileHeight * scale;

      tileWidthPx = tileWidth + 'px';
      tileHeightPx = tileHeight + 'px';
    }

    // Capture the width of the viewport.
    // TODO: It might be possible to only capture this on resize events.
    vpWidth = parseInt($viewportDiv[0].style.width, 10);
    vpHeight = parseInt($viewportDiv[0].style.height, 10);

    // add / subtract 1 tile to ensure coverage around the full viewing area, even when rotated.
    tileL = Math.floor(vpState.left / viewportZoom / tileWidth) - FRAME_SIZE;
    tileR = Math.ceil(tileL + vpWidth / tileWidth) + FRAME_SIZE;
    tileT = Math.floor(vpState.top / viewportZoom / tileHeight) - FRAME_SIZE;
    tileB = Math.ceil(tileT + vpHeight / tileHeight) + FRAME_SIZE;

    // useful for debugging high tile count situations. High tile counts = lots of http requests = slow viewer!
    // const cols = (tileR - tileL + 1);
    // const rows = (tileB - tileT + 1);
    // const tileCount = cols * rows;
    // if (tileCount > 50) {
    //   console.warn(cols, rows, tileCount);
    // }

    // Hide tiles that have moved offscreen. Note we do this before adding new ones to reduce the loop size.
    // TODO: Evaluate the effect of destructuring on performance.
    for (let [pos, tile] of layer.tileBuffer.items) {
      // storing tile position in a string results in a large number of string operations and accounts for nearly
      // 30% of all processing in this function; consider storing / retrieving more efficiently.
      const [x, y] = pos.split(',');
      if (x < tileL || x > tileR || y < tileT || y > tileB) {
        tile.remove();
      }
    }

    // Add tiles to the container if they're in view.
    for (x = tileL; x <= tileR; x++) {
      for (y = tileT; y <= tileB; y++) {
        tilePosition = x + ',' + y;
        tile = layer.tileBuffer.get(tilePosition);
        if (!tile) {
          tile = makeTile(urlBuilder.region(x * xTileWidth, y * xTileHeight, xTileWidth, xTileHeight, pyramidZoom));
          layer.tileBuffer.set(tilePosition, tile);
        }
        tile.load();
        img = tile.img;

        if (zoomChanged) {
          tileLeft = x * tileWidth;
          tileTop = y * tileHeight;

          // In theory, scaling using css transforms is faster since it can leverage the GPU, but the scale
          // rule resizes the image from the center which means we'd need more work on the translation algorithm.
          // For now just use left/right to position.
          img.style.transform = `translate(${tileLeft}px, ${tileTop}px)`; // scale(${scale}, ${scale})`;
          img.style.width = tileWidthPx;
          img.style.height = tileHeightPx;
        }

        if (!img.parentElement) {
          container.appendChild(img);
        }
      }
    }

    // Store off the last layer so we can tell when we switch.
    lastLayer = layer;
  };
}

export const getClosestLayerFn = layers => {
  const layerZooms = Object.keys(layers).map(Number).sort((a, b) => a - b);
  const closestCache = {};

  return (zoom) => {
    if (!closestCache[zoom]) {
      let closestZoom = Number.MAX_VALUE;
      for (let i = 0; i < layerZooms.length; i++) {
        const layerZoom = layerZooms[i];
        if (zoom >= layerZoom) {
          closestZoom = layerZoom;
        }
      }
      closestCache[zoom] = layers[closestZoom] || layers[layerZooms[0]];
    }
    return closestCache[zoom];
  };
};

function alignToTileBoundary (size) {
  if (size < MINIMUM_TILE_SIZE) {
    size = Math.ceil(MINIMUM_TILE_SIZE / size) * size;
  }
  return size;
}



// WEBPACK FOOTER //
// ./src/uScope/js/lib/services/rendering/ViewportTileLayer.js